/**
 * InsuranceQuestionsCard - Phase 3.2 Accountant Dashboard Enhancement
 * 
 * Shows AI-generated likely adjuster questions with pre-populated answers.
 * Allows copy-to-clipboard for quick responses.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import {
  MessageCircleQuestion,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Sparkles,
  BookOpen
} from 'lucide-react';

interface InsuranceQuestion {
  id: string;
  question: string;
  suggested_answer: string;
  category: 'revenue' | 'expenses' | 'causation' | 'documentation';
  confidence: number;
  sources: string[];
}

interface InsuranceQuestionsCardProps {
  reportId: string;
  className?: string;
  /** Pre-fetched slice from /dashboard/case-details. When provided, skips
   *  the per-widget fetch. AttorneyQueue keeps the original behaviour by
   *  not passing this prop. */
  data?: { questions?: InsuranceQuestion[]; error?: string } | null;
  isLoading?: boolean;
}

const CATEGORY_STYLES = {
  revenue: { bg: 'transparent', text: 'var(--color-verified)' },
  expenses: { bg: 'var(--color-needs-human-tint)', text: 'var(--color-needs-human-text)' },
  causation: { bg: 'var(--color-needs-human-tint)', text: 'var(--color-needs-human-text)' },
  documentation: { bg: 'var(--color-needs-human-tint)', text: 'var(--color-needs-human-text)' }
};

export default function InsuranceQuestionsCard({
  reportId,
  className = '',
  data: sliceData,
  isLoading: externalLoading,
}: InsuranceQuestionsCardProps) {
  const { getToken } = useAuth();
  const [questions, setQuestions] = useState<InsuranceQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const batchedMode = sliceData !== undefined;

  useEffect(() => {
    if (batchedMode) return; // parent owns the fetch
    if (reportId) fetchQuestions();
  }, [reportId, batchedMode]);

  const effectiveQuestions: InsuranceQuestion[] = batchedMode
    ? (sliceData?.questions || [])
    : questions;
  const effectiveLoading = batchedMode ? !!externalLoading : loading;
  const effectiveError = batchedMode
    ? !!(sliceData && 'error' in (sliceData || {}) && sliceData.error)
    : error;

  const fetchQuestions = async () => {
    setLoading(true);
    setError(false);
    try {
      const token = await getToken();
      const data = await api.get(`/api/v1/dashboard/insurance-questions/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setQuestions(data.questions || []);
    } catch (err) {
      console.debug('Insurance questions API not available');
      setQuestions([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, questionId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(questionId);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (effectiveLoading) {
    return (
      <div className={`rounded-xl shadow-sm border p-6 ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl shadow-sm border ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div className="p-4 border-b" style={{ borderColor: 'var(--color-border-sub)' }}>
        <div className="flex items-center">
          <MessageCircleQuestion className="w-5 h-5 mr-2" style={{ color: 'var(--color-needs-human-text)' }} />
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Likely Adjuster Questions</h3>
          <span className="ml-2 px-2 py-0.5 text-xs rounded-full flex items-center" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
            <Sparkles className="w-3 h-3 mr-1" />
            AI Generated
          </span>
        </div>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
          Pre-populated answers based on claim data
        </p>
      </div>

      {/* Questions list */}
      <div className="divide-y divide-[color:var(--color-border-sub)]">
        {effectiveError && effectiveQuestions.length === 0 ? (
          <div className="p-6 text-center">
            <MessageCircleQuestion className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--color-text-dim)' }} />
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Questions unavailable</p>
            {!batchedMode && (
              <button
                onClick={fetchQuestions}
                className="mt-2 text-xs"
                style={{ color: 'var(--color-needs-human-text)' }}
              >
                Retry
              </button>
            )}
          </div>
        ) : effectiveQuestions.length === 0 ? (
          <div className="p-8 text-center" style={{ color: 'var(--color-text-muted)' }}>
            <MessageCircleQuestion className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--color-text-dim)' }} />
            <p>No questions generated yet</p>
          </div>
        ) : (
          effectiveQuestions.map((q) => {
            const categoryStyle = CATEGORY_STYLES[q.category] || CATEGORY_STYLES.documentation;
            const isExpanded = expanded === q.id;

            return (
              <div key={q.id} className="p-4">
                <button
                  onClick={() => setExpanded(isExpanded ? null : q.id)}
                  className="w-full text-left"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: categoryStyle.bg, color: categoryStyle.text }}>
                          {q.category}
                        </span>
                        <span className="ml-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                          {Math.round(q.confidence * 100)}% confidence
                        </span>
                      </div>
                      <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        {q.question}
                      </h4>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 ml-2 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
                    ) : (
                      <ChevronDown className="w-5 h-5 ml-2 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
                    )}
                  </div>
                </button>

                {/* Expanded answer */}
                {isExpanded && (
                  <div className="mt-4">
                    <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Suggested Answer</span>
                        <button
                          onClick={() => copyToClipboard(q.suggested_answer, q.id)}
                          className="flex items-center px-2 py-1 text-sm rounded transition-colors"
                          style={{ color: 'var(--color-needs-human-text)' }}
                        >
                          {copied === q.id ? (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4 mr-1" />
                              Copy
                            </>
                          )}
                        </button>
                      </div>
                      <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                        {q.suggested_answer}
                      </p>
                    </div>

                    {/* Sources */}
                    {q.sources.length > 0 && (
                      <div className="mt-3 flex items-center text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        <BookOpen className="w-3 h-3 mr-1" />
                        Sources: {q.sources.join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
